import * as VueRouter from "vue-router"
import { useUserStore } from "./store/user"

// Helper function for lazy loading
const page = (path: string) => () => import(`./components/${path}.vue`)

const routes = [
    // Public routes
    { path: '/', component: page('AppHome') },
    { path: '/about', component: page('AppAbout') },
    { path: '/login', component: page('AppLogin') },

    // Protected dashboard routes
    {
        name: 'Dashboard',
        path: '/dashboard',
        component: page('AppDashboard'),
        meta: { requiresLogin: true, hideNavigation: true },
        redirect: '/dashboard/profile',
        children: [
            { path: 'profile', component: page('dashboard/profile/ProfileIndex') },
            { path: 'preferences', component: page('dashboard/preferences/PreferencesIndex') },
            { path: 'pro', component: page('dashboard/pro/ProIndex') },
            { path: 'widgets', component: page('dashboard/widgets/WidgetsIndex') },
            { path: 'in-game', component: page('dashboard/in-game/InGameIndex') },
            { path: 'starting-soon', component: page('dashboard/starting-soon/StartingSoonIndex') },
            { path: 'intermission', component: page('dashboard/intermission/IntermissionIndex') },
            { path: 'chatbot', component: page('dashboard/chatbot/ChatbotIndex') },
            { path: 'reactions', component: page('dashboard/reactions/ReactionsIndex') },
            { path: 'tools', redirect: '/dashboard/tools/auto-category' },
            { path: 'tools/auto-category', component: page('dashboard/tools/ToolsIndex') },
            { path: 'tools/going-live', component: page('dashboard/tools/GoingLiveIndex') },
            { path: 'tools/subathon', component: page('dashboard/tools/SubathonIndex') },
            { path: 'tools/stream-schedule', component: page('dashboard/tools/StreamScheduleIndex') },
            {
                path: 'admin/invitations',
                component: page('dashboard/admin/AdminInvitations'),
                meta: { requiresAdmin: true },
            },
        ]
    },

    // Widget routes
    {
        path: '/widgets',
        meta: { hideNavigation: true },
        children: [
            { path: 'socials', component: page('widgets/displays/SocialsDisplay') },
            { path: 'summary', component: page('widgets/displays/SummaryDisplay') },
            { path: 'screenshots', component: page('widgets/displays/ScreenshotsDisplay') },
            { path: 'game-cover', component: page('widgets/displays/GameCoverDisplay') },
            { path: 'videos', component: page('widgets/displays/VideoDisplay') },
            { path: 'chat', component: page('widgets/displays/ChatDisplay') },
            { path: 'shoutout', component: page('widgets/displays/ShoutoutDisplay') },
            { path: 'clips', component: page('widgets/displays/ClipsDisplay') },
            { path: 'subathon', component: page('widgets/displays/SubathonDisplay') },
            { path: 'steam-overview', component: page('widgets/displays/SteamOverviewDisplay') },
            { path: 'stream-schedule', component: page('widgets/displays/StreamScheduleDisplay') },
            { path: 'poll', component: page('widgets/displays/PollDisplay') },
        ]

    },
    // Overlays routes
    {
        path: '/overlays',
        meta: { hideNavigation: true },
        children: [
            { path: 'starting-soon', component: page('widgets/displays/StartingSoonDisplay') },
            { path: 'intermission', component: page('widgets/displays/IntermissionDisplay') },
            { path: 'in-game', component: page('widgets/displays/InGameDisplay') },
            { path: 'reactions', component: page('widgets/displays/ReactionsDisplay') },
            { path: 'going-live', component: page('widgets/displays/GoingLiveDisplay') },
        ]

    },

    // Tools routes
    {
        path: '/tools',
        meta: { hideNavigation: true },
        children: [
            { path: 'reaction-listener', component: page('tools/ReactionListener') }
        ]
    },
    {
        path: '/auth-callback',
        name: 'AuthCallback',
        component: () => import('@/components/AuthCallback.vue')
    }
]

const router = VueRouter.createRouter({
    history: VueRouter.createWebHistory(),
    routes,
    scrollBehavior() {
        return { left: 0, top: 0 }
    },
})

router.beforeEach((to, from) => {
    const store = useUserStore()
    if (to.matched.some((record) => record.meta.requiresLogin)) {
        if (store.userRecord == null || store.token.length == 0) {
            return { path: "/login" }
        }
    }
    // UI-only gate — real enforcement is on /api/admin/*
    if (to.matched.some((record) => record.meta.requiresAdmin)) {
        if (!(store.userRecord as { admin?: boolean } | null)?.admin) {
            return { path: "/dashboard/profile" }
        }
    }
    return true
})

export default router